import React, { useState } from 'react';
import { 
  Tag, 
  MapPin, 
  Building, 
  Sparkles, 
  Calendar, 
  Users,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Edit3,
  Save,
  X,
  Loader2
} from 'lucide-react';
import { GlassCard, PillButton, StatusChip } from './IOSComponents';
import { apiClient } from '@/lib/api';
import { toast } from 'sonner';
import '@/styles/ios26-design-system.css';

/**
 * ThemeValidationEditor Component
 * ================================
 * Composant réutilisable pour l'édition et validation des thèmes marketing
 * Inspiré du UI legacy de ProjectSettings.js mais adapté au style iOS 26
 * 
 * Props:
 * - projectId: ID du projet
 * - themes: Object avec les thèmes extraits
 * - onThemesUpdate: Callback appelé quand les thèmes sont mis à jour
 * - compact: boolean - Mode compact pour intégration dans l'étape guidée
 */
export default function ThemeValidationEditor({ 
  projectId, 
  themes = {}, 
  onThemesUpdate,
  compact = false 
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [editingThemes, setEditingThemes] = useState({});
  const [saving, setSaving] = useState(false);
  const [recrawling, setRecrawling] = useState(false);
  
  const parseThemeList = (value) => {
    return value.split(',').map(s => s.trim()).filter(s => s.length > 0);
  };
  
  const handleStartEdit = () => {
    setEditingThemes({
      destinations: themes.destinations || [],
      monuments: themes.monuments || [],
      experiences: themes.experiences || [],
      editorial_angles: themes.editorial_angles || [],
      seasonality: themes.seasonality || [],
      keywords: themes.keywords || [],
    });
    setIsEditing(true);
  };
  
  const handleCancelEdit = () => {
    setEditingThemes({});
    setIsEditing(false);
  };
  
  const handleSaveThemes = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/projects/${projectId}/themes`, editingThemes);
      onThemesUpdate?.({ ...themes, ...editingThemes });
      setIsEditing(false);
      setEditingThemes({});
      toast.success('Thèmes enregistrés !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };
  
  const handleValidateThemes = async () => {
    setSaving(true);
    try {
      await apiClient.put(`/projects/${projectId}/themes`, { validated: true });
      onThemesUpdate?.({ ...themes, validated: true });
      toast.success('Thèmes validés ! Ils seront utilisés pour la génération de contenu.');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la validation');
    } finally {
      setSaving(false);
    }
  };
  
  const handleRecrawl = async () => {
    setRecrawling(true);
    try {
      await apiClient.post(`/projects/${projectId}/recrawl`);
      toast.success('Ré-analyse en cours... Les nouveaux thèmes apparaîtront dans quelques secondes.');
      // Poll for updates
      setTimeout(async () => {
        try {
          const response = await apiClient.get(`/projects/${projectId}`);
          onThemesUpdate?.(response.data.extracted_themes || {});
        } catch (e) {
          console.error('Failed to refresh themes:', e);
        }
        setRecrawling(false);
      }, 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la ré-analyse');
      setRecrawling(false);
    }
  };
  
  const hasThemes = Object.keys(themes).length > 0 && (
    themes.destinations?.length > 0 ||
    themes.monuments?.length > 0 ||
    themes.experiences?.length > 0 ||
    themes.keywords?.length > 0
  );
  
  // Theme categories configuration
  const themeCategories = [
    { 
      key: 'destinations', 
      label: 'Destinations', 
      icon: MapPin, 
      placeholder: 'Paris, Lyon, Marseille...',
      color: 'hsl(var(--color-primary))'
    },
    { 
      key: 'monuments', 
      label: 'Lieux & monuments', 
      icon: Building, 
      placeholder: 'Tour Eiffel, Louvre...',
      color: 'hsl(270 60% 55%)'
    },
    { 
      key: 'experiences', 
      label: 'Expériences', 
      icon: Sparkles, 
      placeholder: 'Visite guidée, Dégustation...',
      color: 'hsl(var(--color-warning))'
    },
    { 
      key: 'editorial_angles', 
      label: 'Angles éditoriaux', 
      icon: Tag, 
      placeholder: 'Découverte, Conseils pratiques...',
      color: 'hsl(var(--color-success))'
    },
    { 
      key: 'seasonality', 
      label: 'Saisonnalité', 
      icon: Calendar, 
      placeholder: 'Été, Noël, Vacances scolaires...',
      color: 'hsl(200 70% 50%)'
    },
    { 
      key: 'keywords', 
      label: 'Mots-clés SEO', 
      icon: Tag, 
      placeholder: 'marketing, digital, innovation...',
      color: 'hsl(var(--color-info))'
    },
  ];
  
  return (
    <div className="space-y-4">
      {/* Header with Status and Actions */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div 
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: 'hsl(var(--color-info-bg))' }}
          >
            <Tag className="w-5 h-5" style={{ color: 'hsl(var(--color-primary))' }} />
          </div>
          <div>
            <h3 className="font-semibold text-semantic">Thèmes marketing</h3>
            <p className="text-xs text-semantic-muted">Personnalisez les thèmes détectés</p>
          </div>
        </div>
        
        {/* Status Badge */}
        {hasThemes && (
          themes.validated ? (
            <StatusChip status="success">
              <CheckCircle className="w-3 h-3 mr-1" />
              Validés
            </StatusChip>
          ) : (
            <StatusChip status="warning">
              <AlertCircle className="w-3 h-3 mr-1" />
              À valider
            </StatusChip>
          )
        )}
      </div>
      
      {/* No themes state */}
      {!hasThemes ? (
        <div 
          className="text-center py-8 rounded-xl"
          style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
        >
          <AlertCircle 
            className="w-12 h-12 mx-auto mb-3" 
            style={{ color: 'hsl(var(--color-text-muted))' }} 
          />
          <p className="text-semantic-muted mb-4">
            Aucun thème détecté. Lancez une analyse de votre site.
          </p>
          <PillButton
            variant="outline"
            onClick={handleRecrawl}
            loading={recrawling}
            icon={<RefreshCw className="w-4 h-4" />}
          >
            {recrawling ? 'Analyse en cours...' : 'Analyser le site'}
          </PillButton>
        </div>
      ) : (
        <>
          {/* Theme Categories Grid */}
          <div className={`grid gap-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
            {themeCategories.map(({ key, label, icon: Icon, placeholder, color }) => (
              <div 
                key={key}
                className="p-4 rounded-xl"
                style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
              >
                <label className="flex items-center gap-2 text-sm font-medium text-semantic mb-2">
                  <Icon className="w-4 h-4" style={{ color }} />
                  {label}
                </label>
                
                {isEditing ? (
                  <textarea
                    value={editingThemes[key]?.join(', ') || ''}
                    onChange={(e) => setEditingThemes(prev => ({
                      ...prev,
                      [key]: parseThemeList(e.target.value)
                    }))}
                    placeholder={placeholder}
                    className="w-full input-semantic resize-none min-h-[60px]"
                    data-testid={`theme-${key}-input`}
                  />
                ) : (
                  <div className="flex flex-wrap gap-1.5 min-h-[40px]">
                    {(themes[key] || []).length > 0 ? (
                      themes[key].map((item, i) => (
                        <span
                          key={i}
                          className="px-2.5 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            backgroundColor: 'hsl(var(--color-info-bg))',
                            color: 'hsl(var(--color-info-text))'
                          }}
                        >
                          {item}
                        </span>
                      ))
                    ) : (
                      <span className="text-xs text-semantic-muted italic">Non défini</span>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {/* Brand Tone & Target Audience (read-only) */}
          {(themes.brand_tone || themes.target_audience) && (
            <div className={`grid gap-4 ${compact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
              {themes.brand_tone && (
                <div 
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
                >
                  <label className="flex items-center gap-2 text-sm font-medium text-semantic mb-2">
                    <Users className="w-4 h-4" style={{ color: 'hsl(var(--color-primary))' }} />
                    Ton de la marque
                  </label>
                  <p className="text-sm text-semantic-2">{themes.brand_tone}</p>
                </div>
              )}
              {themes.target_audience && (
                <div 
                  className="p-4 rounded-xl"
                  style={{ backgroundColor: 'hsl(var(--color-surface-2))' }}
                >
                  <label className="flex items-center gap-2 text-sm font-medium text-semantic mb-2">
                    <Users className="w-4 h-4" style={{ color: 'hsl(270 60% 55%)' }} />
                    Audience cible
                  </label>
                  <p className="text-sm text-semantic-2">{themes.target_audience}</p>
                </div>
              )}
            </div>
          )}
          
          {/* Action Buttons */}
          <div className="flex flex-wrap gap-3 pt-2">
            {isEditing ? (
              <>
                <PillButton
                  variant="primary"
                  onClick={handleSaveThemes}
                  loading={saving}
                  icon={<Save className="w-4 h-4" />}
                  data-testid="save-themes-btn"
                >
                  {saving ? 'Enregistrement...' : 'Enregistrer'}
                </PillButton>
                <PillButton
                  variant="outline"
                  onClick={handleCancelEdit}
                  icon={<X className="w-4 h-4" />}
                >
                  Annuler
                </PillButton>
              </>
            ) : (
              <>
                <PillButton
                  variant="outline"
                  onClick={handleStartEdit}
                  icon={<Edit3 className="w-4 h-4" />}
                  data-testid="edit-themes-btn"
                >
                  Modifier
                </PillButton>
                <PillButton
                  variant="outline"
                  onClick={handleRecrawl}
                  loading={recrawling}
                  icon={<RefreshCw className="w-4 h-4" />}
                  data-testid="recrawl-btn"
                >
                  {recrawling ? 'Analyse...' : 'Ré-analyser'}
                </PillButton>
                {!themes.validated && (
                  <PillButton
                    variant="primary"
                    onClick={handleValidateThemes}
                    loading={saving}
                    icon={<CheckCircle className="w-4 h-4" />}
                    data-testid="validate-themes-btn"
                  >
                    Valider les thèmes
                  </PillButton>
                )}
              </>
            )}
          </div>
          
          {/* Last crawl timestamp */}
          {themes.last_crawl && (
            <p className="text-xs text-semantic-muted">
              Dernière analyse : {new Date(themes.last_crawl).toLocaleString('fr-FR')}
            </p>
          )}
        </>
      )}
    </div>
  );
}
